import { useState } from 'react';
import { Plus, Tags, Pencil, Trash2, Sparkles, TrendingUp, TrendingDown, Hash } from 'lucide-react';
import { useCategories } from '@hooks/useCategories';
import PageHeader from '@components/layout/PageHeader';
import Card from '@components/common/Card';
import Button from '@components/common/Button';
import Modal from '@components/common/Modal';
import Loading from '@components/common/Loading';
import EmptyState from '@components/common/EmptyState';
import PageTutorial from '@components/common/PageTutorial';
import CategoryForm from '@components/categories/CategoryForm';
import { clsx } from 'clsx';

// ─── MetricCard ───────────────────────────────────────────────────────────────
const MetricCard = ({ title, value, subtitle, icon, iconBg, valueColor }) => (
  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
    <div className="flex items-start justify-between mb-3">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{title}</p>
      <div className={clsx('p-2.5 rounded-xl', iconBg)}>{icon}</div>
    </div>
    <p className={clsx('text-2xl font-bold', valueColor)}>{value}</p>
    {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
  </div>
);

const Categories = () => {
  const {
    incomeCategories,
    expenseCategories,
    isLoading,
    createCategory,
    updateCategory,
    deleteCategory,
    seedDefaults,
    isCreating,
    isUpdating,
    isDeleting,
  } = useCategories();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSeedLoading, setIsSeedLoading] = useState(false);

  const handleOpenModal = (category = null) => {
    setSelectedCategory(category);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedCategory(null);
    setIsModalOpen(false);
  };

  const handleSubmit = async (data) => {
    if (selectedCategory) {
      await updateCategory({ categoryId: selectedCategory.categoryId, data });
    } else {
      await createCategory(data);
    }
    handleCloseModal();
  };

  const handleDelete = async () => {
    if (selectedCategory) {
      await deleteCategory(selectedCategory.categoryId);
      setIsDeleteModalOpen(false);
      setSelectedCategory(null);
    }
  };

  const openDeleteModal = (category) => {
    setSelectedCategory(category);
    setIsDeleteModalOpen(true);
  };

  const handleSeedDefaults = async () => {
    setIsSeedLoading(true);
    try {
      await seedDefaults();
    } finally {
      setIsSeedLoading(false);
    }
  };

  const CategoryCard = ({ category }) => (
    <div className="flex items-center justify-between p-4 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary-400 dark:hover:border-primary-500 hover:shadow-sm transition-all group">
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
          style={{ backgroundColor: `${category.color}20` }}
        >
          {category.icon}
        </div>
        <div>
          <p className="font-medium text-gray-900 dark:text-white">{category.name}</p>
          <p className="text-xs text-gray-400 mt-0.5">
            {category.transactionCount || 0} transaç{category.transactionCount === 1 ? 'ão' : 'ões'}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => handleOpenModal(category)}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <Pencil size={15} className="text-gray-400" />
        </button>
        <button
          onClick={() => openDeleteModal(category)}
          className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
        >
          <Trash2 size={15} className="text-red-500" />
        </button>
      </div>
    </div>
  );

  if (isLoading) {
    return <Loading text="Carregando categorias..." />;
  }

  const hasCategories = incomeCategories.length > 0 || expenseCategories.length > 0;
  const totalTransactions =
    [...incomeCategories, ...expenseCategories].reduce((s, c) => s + (c.transactionCount || 0), 0);

  return (
    <div className="space-y-6 pb-8">
      <PageHeader
        title="Categorias"
        subtitle="Organize suas transações por categorias"
        action={
          <div className="flex gap-2">
            {!hasCategories && (
              <Button
                variant="secondary"
                leftIcon={<Sparkles size={18} />}
                onClick={handleSeedDefaults}
                isLoading={isSeedLoading}
              >
                Criar Padrões
              </Button>
            )}
            <Button leftIcon={<Plus size={18} />} onClick={() => handleOpenModal()}>
              Nova Categoria
            </Button>
          </div>
        }
      />

      <PageTutorial
        pageId="categories"
        title="Organizando por categorias"
        steps={[
          'Categorias ajudam a classificar suas transacoes em receitas e despesas',
          'Use \'Criar Padroes\' para gerar categorias comuns automaticamente',
          'Cada categoria tem um icone e cor para facilitar a identificacao',
        ]}
      />

      {!hasCategories ? (
        <EmptyState
          icon={<Tags size={32} />}
          title="Nenhuma categoria cadastrada"
          description="Crie categorias para organizar suas transações ou use as categorias padrão."
          action={handleSeedDefaults}
          actionLabel="Criar Categorias Padrão"
        />
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <MetricCard
              title="Categorias de Receita"
              value={incomeCategories.length}
              subtitle="categorias cadastradas"
              icon={<TrendingUp size={18} className="text-emerald-600" />}
              iconBg="bg-emerald-100 dark:bg-emerald-900/30"
              valueColor="text-emerald-600"
            />
            <MetricCard
              title="Categorias de Despesa"
              value={expenseCategories.length}
              subtitle="categorias cadastradas"
              icon={<TrendingDown size={18} className="text-red-600" />}
              iconBg="bg-red-100 dark:bg-red-900/30"
              valueColor="text-red-600"
            />
            <MetricCard
              title="Total de Transações"
              value={totalTransactions}
              subtitle="em todas as categorias"
              icon={<Hash size={18} className="text-primary-600" />}
              iconBg="bg-primary-100 dark:bg-primary-900/30"
              valueColor="text-gray-900 dark:text-white"
            />
          </div>

          {/* Category Lists */}
          <div className="grid lg:grid-cols-2 gap-6">
            <Card
              title="Receitas"
              subtitle={`${incomeCategories.length} categoria${incomeCategories.length !== 1 ? 's' : ''}`}
            >
              {incomeCategories.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                  Nenhuma categoria de receita
                </p>
              ) : (
                <div className="space-y-3">
                  {incomeCategories.map((category) => (
                    <CategoryCard key={category.categoryId} category={category} />
                  ))}
                </div>
              )}
            </Card>

            <Card
              title="Despesas"
              subtitle={`${expenseCategories.length} categoria${expenseCategories.length !== 1 ? 's' : ''}`}
            >
              {expenseCategories.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                  Nenhuma categoria de despesa
                </p>
              ) : (
                <div className="space-y-3">
                  {expenseCategories.map((category) => (
                    <CategoryCard key={category.categoryId} category={category} />
                  ))}
                </div>
              )}
            </Card>
          </div>
        </>
      )}

      {/* Category Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={selectedCategory ? 'Editar Categoria' : 'Nova Categoria'}
        size="md"
      >
        <CategoryForm
          category={selectedCategory}
          onSubmit={handleSubmit}
          onCancel={handleCloseModal}
          isLoading={isCreating || isUpdating}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Excluir Categoria"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} isLoading={isDeleting}>
              Excluir
            </Button>
          </>
        }
      >
        <p className="text-gray-600 dark:text-gray-400">
          Tem certeza que deseja excluir a categoria <strong>{selectedCategory?.name}</strong>?
          {selectedCategory?.transactionCount > 0 && (
            <span className="block mt-2 text-red-600">
              Esta categoria possui {selectedCategory.transactionCount} transações vinculadas.
            </span>
          )}
        </p>
      </Modal>
    </div>
  );
};

export default Categories;
